// @flow
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InputBase from '@mui/material/InputBase';

import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableChartIcon from '@mui/icons-material/TableChart';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import * as XLSX from 'xlsx';
import { fetchFileBuffer, downloadFileBlob } from './fileViewerUtils';

/**
 * Excel / Spreadsheet Viewer powered by SheetJS (https://docs.sheetjs.com/docs/)
 */
export default function ExcelViewer(props) {
  const { fileUrl, filename, canDownload } = props;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workbook, setWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [fileBlob, setFileBlob] = useState(null);

  const containerRef = useRef(null);
  const tableContainerRef = useRef(null);

  // Load workbook from URL using SheetJS
  const loadWorkbook = useCallback(async () => {
    if (!fileUrl) return;

    setLoading(true);
    setError(null);
    setWorkbook(null);
    setSheetNames([]);
    setActiveSheetIndex(0);
    setSearchQuery('');

    try {
      const { arrayBuffer, blob } = await fetchFileBuffer(fileUrl);
      setFileBlob(blob);

      // Parse with SheetJS
      const wb = XLSX.read(arrayBuffer, {
        type: 'array',
        cellDates: true,
        cellStyles: true,
        cellNF: true,
      });

      if (!wb || !wb.SheetNames || wb.SheetNames.length === 0) {
        throw new Error('No readable sheets found in this Excel file.');
      }

      setWorkbook(wb);
      setSheetNames(wb.SheetNames);
      setActiveSheetIndex(0);
      setLoading(false);
    } catch (err) {
      console.error('SheetJS ExcelViewer error:', err);
      setError(err?.message || 'Failed to parse Excel spreadsheet.');
      setLoading(false);
    }
  }, [fileUrl]);

  useEffect(() => {
    loadWorkbook();
  }, [loadWorkbook]);

  // Current sheet details and HTML generation
  const activeSheetName = sheetNames[activeSheetIndex] || '';
  const currentWorksheet = workbook && activeSheetName ? workbook.Sheets[activeSheetName] : null;

  const sheetStats = useMemo(() => {
    if (!currentWorksheet || !currentWorksheet['!ref']) {
      return { rows: 0, cols: 0 };
    }
    try {
      const range = XLSX.utils.decode_range(currentWorksheet['!ref']);
      return {
        rows: range.e.r - range.s.r + 1,
        cols: range.e.c - range.s.c + 1,
      };
    } catch {
      return { rows: 0, cols: 0 };
    }
  }, [currentWorksheet]);

  const rawHtmlTable = useMemo(() => {
    if (!currentWorksheet) return '';
    try {
      // SheetJS sheet_to_html conversion
      return XLSX.utils.sheet_to_html(currentWorksheet, {
        id: 'sheetjs-table',
        editable: false,
      });
    } catch (err) {
      console.error('Failed to generate HTML from sheet:', err);
      return '<div class="sheet-error">Failed to render worksheet HTML.</div>';
    }
  }, [currentWorksheet]);

  // Highlight search terms inside table HTML
  const processedHtml = useMemo(() => {
    if (!rawHtmlTable) return '';
    const trimmed = searchQuery.trim();
    if (!trimmed) return rawHtmlTable;

    try {
      const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(?![^<]*>)(${escaped})`, 'gi');
      return rawHtmlTable.replace(regex, '<mark class="excel-search-hit">$1</mark>');
    } catch {
      return rawHtmlTable;
    }
  }, [rawHtmlTable, searchQuery]);

  // Zoom controls
  const handleZoomIn = () => setZoom((prev) => Math.min(Number((prev + 0.1).toFixed(1)), 2.0));
  const handleZoomOut = () => setZoom((prev) => Math.max(Number((prev - 0.1).toFixed(1)), 0.5));
  const handleResetZoom = () => setZoom(1);

  // Tab change
  const handleTabChange = (event, newIndex) => {
    setActiveSheetIndex(newIndex);
  };

  // Download handler
  const handleDownload = () => {
    if (fileBlob) {
      downloadFileBlob(fileBlob, filename || 'spreadsheet.xlsx');
    } else if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  // Export current sheet as CSV
  const handleExportCsv = () => {
    if (!currentWorksheet) return;
    try {
      const csvContent = XLSX.utils.sheet_to_csv(currentWorksheet);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const csvName = `${(filename || 'spreadsheet').replace(/\.[^/.]+$/, '')}_${activeSheetName || 'sheet'}.csv`;
      downloadFileBlob(blob, csvName);
    } catch (err) {
      console.error('CSV export failed:', err);
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        bgcolor: '#f8fafc',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Top Main Toolbar */}
      <Paper
        elevation={2}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 0.75,
          bgcolor: '#107c41',
          color: '#ffffff',
          borderRadius: 0,
          zIndex: 10,
          flexShrink: 0,
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {/* Left: Title & Stats */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TableChartIcon sx={{ fontSize: 20, color: '#dcfce7' }} />
          <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600, fontSize: '0.85rem' }}>
            {filename || 'Excel Spreadsheet'}
          </Typography>

          {sheetNames.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: '#dcfce7',
                bgcolor: '#0b532c',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontWeight: 500,
                display: { xs: 'none', sm: 'inline-block' },
              }}
            >
              {sheetNames.length} {sheetNames.length === 1 ? 'Sheet' : 'Sheets'}
            </Typography>
          )}

          {sheetStats.rows > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: '#dcfce7',
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                display: { xs: 'none', md: 'inline-block' },
              }}
            >
              {sheetStats.rows} rows × {sheetStats.cols} cols
            </Typography>
          )}
        </Box>

        {/* Right: Search, Zoom, Export, Reload */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Search Bar */}
          {searchOpen ? (
            <Paper
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                py: 0.2,
                bgcolor: '#ffffff',
                borderRadius: 1,
                height: 28,
              }}
            >
              <SearchIcon sx={{ color: '#64748b', fontSize: 18, mr: 0.5 }} />
              <InputBase
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Find in sheet..."
                autoFocus
                sx={{ fontSize: '0.8rem', width: { xs: 100, sm: 140 } }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  setSearchQuery('');
                  setSearchOpen(false);
                }}
                sx={{ p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Paper>
          ) : (
            <Tooltip title="Search / Find in Sheet">
              <IconButton
                size="small"
                onClick={() => setSearchOpen(true)}
                sx={{ color: '#ffffff', '&:hover': { bgcolor: '#0b532c' } }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)', mx: 0.5, height: 20, my: 'auto' }} />

          {/* Zoom Controls */}
          <Tooltip title="Zoom Out (-10%)">
            <span>
              <IconButton
                size="small"
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
                sx={{ color: '#ffffff', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Typography variant="caption" sx={{ color: '#ffffff', minWidth: 40, textAlign: 'center', fontWeight: 600 }}>
            {Math.round(zoom * 100)}%
          </Typography>

          <Tooltip title="Zoom In (+10%)">
            <span>
              <IconButton
                size="small"
                onClick={handleZoomIn}
                disabled={zoom >= 2.0}
                sx={{ color: '#ffffff', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {zoom !== 1 && (
            <Tooltip title="Reset Zoom">
              <IconButton size="small" onClick={handleResetZoom} sx={{ color: '#ffffff' }}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)', mx: 0.5, height: 20, my: 'auto' }} />

          {/* Export to CSV */}
          <Tooltip title="Export Current Sheet to CSV">
            <IconButton
              size="small"
              onClick={handleExportCsv}
              sx={{ color: '#ffffff', '&:hover': { bgcolor: '#0b532c' } }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#dcfce7' }}>
                CSV
              </Typography>
            </IconButton>
          </Tooltip>

          {/* Download Original File */}
          {canDownload && (
            <Tooltip title="Download File">
              <IconButton size="small" onClick={handleDownload} sx={{ color: '#ffffff', '&:hover': { bgcolor: '#0b532c' } }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Reload */}
          <Tooltip title="Reload">
            <IconButton size="small" onClick={loadWorkbook} sx={{ color: '#ffffff', '&:hover': { bgcolor: '#0b532c' } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: loading || error ? 'center' : 'stretch',
          justifyContent: loading || error ? 'center' : 'flex-start',
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 'auto' }}>
            <CircularProgress size={48} sx={{ color: '#107c41', mb: 2 }} />
            <Typography variant="body1" sx={{ color: '#374151', fontWeight: 500 }}>
              Parsing Excel spreadsheet with SheetJS...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ maxWidth: 540, mx: 'auto', p: 3, my: 'auto' }}>
            <Alert
              severity="error"
              action={
                canDownload ? (
                  <Button color="inherit" size="small" startIcon={<DownloadIcon />} onClick={handleDownload}>
                    Download File
                  </Button>
                ) : null
              }
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && workbook && (
          <Box
            ref={tableContainerRef}
            sx={{
              flexGrow: 1,
              width: '100%',
              height: '100%',
              overflow: 'auto',
              p: 2,
              bgcolor: '#ffffff',
              boxSizing: 'border-box',
              '& #sheetjs-table': {
                borderCollapse: 'collapse',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                fontSize: '13px',
                color: '#1f2937',
                width: 'max-content',
                minWidth: '100%',
                transformOrigin: 'top left',
                transform: `scale(${zoom})`,
                transition: 'transform 0.15s ease-out',
                '& tr': {
                  height: '26px',
                },
                '& tr:first-of-type td, & tr:first-of-type th': {
                  fontWeight: 600,
                  bgcolor: '#f1f5f9',
                  color: '#0f172a',
                },
                '& td, & th': {
                  border: '1px solid #d1d5db',
                  padding: '4px 10px',
                  whiteSpace: 'pre',
                  textAlign: 'left',
                  verticalAlign: 'middle',
                  minWidth: '60px',
                  minHeight: '24px',
                  lineHeight: 1.4,
                  userSelect: 'text',
                },
                '& tr:hover td': {
                  bgcolor: '#f8fafc',
                },
                '& td:focus-within, & td:hover': {
                  outline: '1px solid #107c41',
                },
              },
              '& .excel-search-hit': {
                bgcolor: '#fef08a',
                color: '#854d0e',
                fontWeight: 700,
                padding: '1px 3px',
                borderRadius: '2px',
              },
              '& .sheet-error': {
                p: 3,
                color: '#dc2626',
                fontStyle: 'italic',
              },
            }}
            dangerouslySetInnerHTML={{ __html: processedHtml }}
          />
        )}
      </Box>

      {/* Bottom Worksheet Tabs Bar (Excel Style) */}
      {!loading && !error && sheetNames.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#f1f5f9',
            borderTop: '1px solid #e2e8f0',
            borderRadius: 0,
            flexShrink: 0,
            px: 1,
            zIndex: 10,
          }}
        >
          <Tabs
            value={activeSheetIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 34,
              '& .MuiTabs-indicator': {
                backgroundColor: '#107c41',
                height: 3,
              },
              '& .MuiTab-root': {
                minHeight: 34,
                py: 0.5,
                px: 2,
                fontSize: '0.8rem',
                textTransform: 'none',
                fontWeight: 500,
                color: '#64748b',
                borderRight: '1px solid #e2e8f0',
                '&.Mui-selected': {
                  color: '#107c41',
                  fontWeight: 700,
                  bgcolor: '#ffffff',
                },
                '&:hover': {
                  bgcolor: '#f8fafc',
                },
              },
            }}
          >
            {sheetNames.map((name, index) => (
              <Tab key={`${name}-${index}`} label={name} />
            ))}
          </Tabs>
        </Paper>
      )}
    </Box>
  );
}

ExcelViewer.propTypes = {
  fileUrl: PropTypes.string.isRequired,
  filename: PropTypes.string,
  canDownload: PropTypes.bool,
};

ExcelViewer.defaultProps = {
  filename: '',
  canDownload: true,
};
